import {
  SelectFilterInternalArguments,
  SelectFilterValue,
  useSelectFilter,
} from './fundamentals'
import React, { useMemo } from 'react'
import { useGetProjectLocations } from '@core/react-query/features/projects/locations'
import { FilterHookArgumentsBase } from '@core/providers/filters'
import { ProjectLocationNode } from '@core/entities/project/ProjectLocationNode/ProjectLocationNode'
import { localizedStrings } from '@core/strings'

export type ProjectLocationBoundariesFilterItem = {
  node: ProjectLocationNode
  depth: number
}

type ProjectLocationBoundariesFilterArgs = FilterHookArgumentsBase<
  SelectFilterValue<string, false>
> & {
  projectId: number
  renderItem?: (item: ProjectLocationBoundariesFilterItem) => React.ReactElement
}

/**
 * Single-select filter over the location tree, indented by depth and limited
 * to locations with a boundary in their subtree. Hidden when the hierarchy
 * is disabled or no location has a boundary.
 */
export const useProjectLocationBoundariesFilter: (
  args: ProjectLocationBoundariesFilterArgs,
) => SelectFilterInternalArguments<string, false> = ({
  renderItem,
  ...args
}) => {
  const { data: projectLocations } = useGetProjectLocations(
    {
      projectId: args.projectId,
      statuses: ['ACTIVE'],
    },
    { enabled: !!args.projectId },
  )

  const locationRows = useMemo(() => {
    if (!projectLocations?.enabled) {
      return []
    }
    return ProjectLocationNode.flattenNodes(projectLocations.locations).filter(
      ({ node }) => node.hasBoundaryInSubtree(),
    )
  }, [projectLocations])

  const rowsByNodeId = useMemo(() => {
    return new Map(locationRows.map((row) => [row.node.id, row]))
  }, [locationRows])

  const isHidden =
    args.isHidden || !projectLocations?.enabled || !locationRows.length

  return useSelectFilter({
    nothingSelectedText: localizedStrings.allLocations,
    ...args,
    options: locationRows.map(({ node }) => node.id),
    isHidden,
    clearIfValueIsNotInOptions: true,
    renderItem: renderItem
      ? (value: string) => {
          const row = rowsByNodeId.get(value)
          return row ? renderItem(row) : <></>
        }
      : undefined,
    valueToLabel: (v) => rowsByNodeId.get(v)?.node.name,
  })
}
